"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_NET_PREFERENCE_CONFIGS = void 0;
exports.createNetTypeCondition = createNetTypeCondition;
/**
 * Helper function to create a condition that matches nets by their net_type property.
 * @param netType The net type to match (e.g., "High Speed", "Power", "Analog")
 */
function createNetTypeCondition(netType) {
    return (documentNet) => {
        const netTypeProperty = Object.values(documentNet.properties).find((prop) => prop.name === "Net Type");
        return netTypeProperty?.value === netType;
    };
}
/**
 * Default net preference configurations based on net type property.
 * These map to the behaviors defined in the routing preferences table.
 *
 * Each configuration specifies:
 * - id: Unique identifier for the preference group
 * - description: Human-readable explanation of what nets this applies to
 * - condition: Predicate function to match nets
 * - coefficients: DeepPCB routing preference values (default = 1)
 *   - reduceViaCountPrioCoef: Higher = fewer vias
 *   - reduceWireLengthPrioCoef: Higher = shorter traces
 *   - reduceAcuteAnglePrioCoef: Higher = smoother routing, fewer sharp angles
 */
exports.DEFAULT_NET_PREFERENCE_CONFIGS = [
    {
        id: "high-speed-nets",
        description: "High speed nets - minimize vias, short traces, smooth routing",
        condition: createNetTypeCondition("High Speed"),
        coefficients: {
            reduceViaCountPrioCoef: 3, // Don't place vias (soft) = 3
            reduceWireLengthPrioCoef: 3, // Reduce wire length = 3
            reduceAcuteAnglePrioCoef: undefined, // "N/A"
        },
    },
    {
        id: "medium-speed-nets",
        description: "Medium speed nets - focus on smooth routing",
        condition: createNetTypeCondition("Medium Speed"),
        coefficients: {
            reduceViaCountPrioCoef: 1, // Default via behavior
            reduceWireLengthPrioCoef: 1, // Default wire length
            reduceAcuteAnglePrioCoef: 2, // Reduce acute angles = 2
        },
    },
    {
        id: "low-speed-nets",
        description: "Low speed nets - slightly avoid vias",
        condition: createNetTypeCondition("Low Speed"),
        coefficients: {
            reduceViaCountPrioCoef: 1, // Avoid vias
            reduceWireLengthPrioCoef: 1, // Default wire length
            reduceAcuteAnglePrioCoef: 1, // Default
        },
    },
    {
        id: "analog-nets",
        description: "Analog nets - prioritize short traces, avoid vias",
        condition: createNetTypeCondition("Analog"),
        coefficients: {
            reduceViaCountPrioCoef: 2, // Avoid vias = 2
            reduceWireLengthPrioCoef: 4, // Very high priority = 4
            reduceAcuteAnglePrioCoef: 2, // Reduce acute angles = 2
        },
    },
    {
        id: "power-nets",
        description: "Power nets - medium priority on trace length",
        condition: createNetTypeCondition("Power"),
        coefficients: {
            reduceViaCountPrioCoef: 1, // Default via behavior
            reduceWireLengthPrioCoef: 2, // Medium priority = 2
            reduceAcuteAnglePrioCoef: 1, // Default
        },
    },
    {
        id: "ground-nets",
        description: "Ground nets - default routing behavior",
        condition: createNetTypeCondition("Ground"),
        coefficients: {
            reduceViaCountPrioCoef: 1, // Default
            reduceWireLengthPrioCoef: 1, // Default
            reduceAcuteAnglePrioCoef: 1, // Default
        },
    },
];
//# sourceMappingURL=netPreferences.js.map